import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { sfQuery } from '../../../API';
import { useAuthContext } from '../../../../contexts/AuthContext';

const ACTIVE_STATUSES = [
    'Active - Initial',
    'Active - Renewed',
    'Active - Additional',
    'Active - Determine Relationship',
    'DM - NEW',
    'Cancellation Pending'
];

const isActiveStatus = (status) => {
    if (!status) return false;
    return ACTIVE_STATUSES.includes(status) || status.startsWith('Active');
};

const getStatusColor = (status) => {
    if (!status) return '#9ca3af';
    if (status.startsWith('Active')) return '#059669';
    if (status === 'Cancellation Pending') return '#d97706';
    if (status.startsWith('Inactive')) return '#dc2626';
    return '#6b7280';
};

/**
 * SalesforceAccountSearch Component
 *
 * A reusable autocomplete search component for Salesforce accounts.
 * Allows users to search for accounts and select them, or manually enter text.
 *
 * @param {string} value - Current input value
 * @param {function} onChange - Callback when input changes (receives text string)
 * @param {function} onAccountSelect - Callback when account is selected (receives account object)
 * @param {string} placeholder - Placeholder text for input
 * @param {boolean} required - Whether field is required
 */
export const SalesforceAccountSearch = ({
    value,
    onChange,
    onAccountSelect,
    placeholder = "Search for a Salesforce account or type manually...",
    required = false
}) => {
    const context = useAuthContext();
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState(null);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const searchTimeoutRef = useRef(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const suppressNextSearchRef = useRef(false);

    const updateDropdownPosition = () => {
        const inputEl = inputRef.current;
        if (!inputEl) return;
        const rect = inputEl.getBoundingClientRect();
        setDropdownStyle({
            position: 'fixed',
            top: rect.bottom + 6,
            left: rect.left,
            width: rect.width,
            zIndex: 2147483000,
            pointerEvents: 'auto'
        });
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && containerRef.current.contains(event.target)) return;
            if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
            setShowDropdown(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search effect
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!value || value.trim().length < 2) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        if (suppressNextSearchRef.current) {
            suppressNextSearchRef.current = false;
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const searchTerm = value.trim().replace(/'/g, "\\'");
                const results = await sfQuery(
                    "Account",
                    "Id, Name, Website, Phone, BillingCity, BillingState, Status__c, RecordType.Name",
                    `Name LIKE '%${searchTerm}%' AND (NOT Name LIKE 'Account to delete%') ORDER BY Name ASC LIMIT 50`,
                    context
                );

                const term = searchTerm.toLowerCase();
                const sorted = (results || []).sort((a, b) => {
                    const aName = a.Name.toLowerCase();
                    const bName = b.Name.toLowerCase();
                    // Active accounts first
                    const aActive = isActiveStatus(a.Status__c);
                    const bActive = isActiveStatus(b.Status__c);
                    if (aActive !== bActive) return aActive ? -1 : 1;
                    // Then exact match
                    const aExact = aName === term;
                    const bExact = bName === term;
                    if (aExact !== bExact) return aExact ? -1 : 1;
                    // Then starts-with
                    const aStarts = aName.startsWith(term);
                    const bStarts = bName.startsWith(term);
                    if (aStarts !== bStarts) return aStarts ? -1 : 1;
                    // Then alphabetical
                    return aName.localeCompare(bName);
                });

                setSuggestions(sorted);
                setHighlightIndex(-1);
                setShowDropdown(sorted.length > 0);
            } catch (error) {
                console.error('Error searching Salesforce:', error);
                setSuggestions([]);
                setShowDropdown(false);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [value, context]);

    // Update dropdown position on open/resize/scroll
    useEffect(() => {
        if (!showDropdown) return;

        updateDropdownPosition();

        const handleResizeScroll = () => updateDropdownPosition();
        window.addEventListener('resize', handleResizeScroll);
        window.addEventListener('scroll', handleResizeScroll, true);

        return () => {
            window.removeEventListener('resize', handleResizeScroll);
            window.removeEventListener('scroll', handleResizeScroll, true);
        };
    }, [showDropdown, value]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightIndex < 0 || !dropdownRef.current) return;
        const items = dropdownRef.current.querySelectorAll('.sf-suggestion-item');
        if (items[highlightIndex]) {
            items[highlightIndex].scrollIntoView({ block: 'nearest' });
        }
    }, [highlightIndex]);

    const handleInputChange = (e) => {
        onChange(e.target.value);
    };

    const selectAccount = (account) => {
        onChange(account.Name);
        suppressNextSearchRef.current = true;
        setShowDropdown(false);
        setSuggestions([]);
        setHighlightIndex(-1);
        if (onAccountSelect) {
            onAccountSelect(account);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowDropdown(false);
            return;
        }

        if (!showDropdown || suggestions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
        } else if (e.key === 'Enter' && highlightIndex >= 0) {
            e.preventDefault();
            selectAccount(suggestions[highlightIndex]);
        }
    };

    const getRecordTypeName = (account) => {
        if (account.RecordType && account.RecordType.Name) {
            return account.RecordType.Name;
        }
        return null;
    };

    return (
        <div className="sf-autocomplete-container" ref={containerRef}>
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="post-planner-input"
                required={required}
            />

            {showDropdown && suggestions.length > 0 && dropdownStyle && createPortal(
                <div
                    ref={dropdownRef}
                    className="sf-autocomplete-suggestions"
                    style={dropdownStyle}
                >
                    {suggestions.map((account, index) => {
                        const recordType = getRecordTypeName(account);
                        const status = account.Status__c;
                        const location = [account.BillingCity, account.BillingState].filter(Boolean).join(', ');

                        return (
                            <div
                                key={account.Id}
                                className={`sf-suggestion-item${index === highlightIndex ? ' sf-suggestion-highlighted' : ''}`}
                                onClick={() => selectAccount(account)}
                                onMouseEnter={() => setHighlightIndex(index)}
                            >
                                <div className="sf-suggestion-top-row">
                                    <div className="sf-suggestion-name">{account.Name}</div>
                                    {status && (
                                        <span
                                            className="sf-suggestion-status"
                                            style={{ color: getStatusColor(status) }}
                                        >
                                            {status}
                                        </span>
                                    )}
                                </div>
                                {(location || recordType) && (
                                    <div className="sf-suggestion-details">
                                        {location && <span className="sf-suggestion-location">{location}</span>}
                                        {location && recordType && <span className="sf-suggestion-separator"> · </span>}
                                        {recordType && <span className="sf-suggestion-record-type">{recordType}</span>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
};
